// Static mock data for the Standard Ordering flow. No backend calls.

import productMaggiCurryImg from '@/assets/images/product-maggi-curry-noodles.png'
import productMarigoldHlImg from '@/assets/images/product-marigold-hl-200ml.png'
import productMiloActivGoImg from '@/assets/images/product-milo-bag-1.2kg.png'
import productMiloCerealImg from '@/assets/images/product-milo-cereal.png'
import productMiloPowderTinImg from '@/assets/images/product-milo-powder-tin.png'
import productNescafeJarImg from '@/assets/images/product-nescafe-classic-jar.png'
import { toISODate } from '@/lib/utils'
import type { RtvCardData } from '@/screens/ordering/components/orders/rtv-card'
import type {
  GoodsReceipt,
  Order,
  QpoItem,
  QpoLine,
  QpoSession,
  QpoSupplier,
  Sku,
  Supplier,
} from '@/screens/ordering/types'
import { randomFutureDate } from './utils/qpo-utils'

export const MOCK_SKUS: [Sku, ...Sku[]] = [
  {
    id: '100055671101',
    name: 'Milo Activ-Go 3-in-1 (30 x 33g)',
    barcode: '9556001234567',
    unit: 'CAR',
    supplier: 'Nestlé Malaysia',
    warehouse: 'Joo Koon DC (68)',
    swatch: '#7CCC78',
    image: productMiloActivGoImg,
    onOffer: true,
    inPlanogram: true,
    casePack: 24,
    pending: 12,
    fnr: 48,
    transit: 6,
    soh: 32,
  },
  {
    id: '100055671102',
    name: 'Milo Original Powder Refill 1kg',
    barcode: '9556001234574',
    unit: 'CAR',
    supplier: 'Nestlé Malaysia',
    warehouse: 'Joo Koon DC (68)',
    swatch: '#2E5CD1',
    image: productMiloPowderTinImg,
    onOffer: false,
    inPlanogram: true,
    casePack: 12,
    pending: 0,
    fnr: 24,
    transit: 0,
    soh: 18,
  },
  {
    // The Articlepedia list and detail screens render this product, so the
    // add-to-cart sheet opened over them must resolve to the same SKU. Sits at
    // index 2 so `?cart=3` seeds it as the lesson's third item.
    id: '10054459',
    name: 'MARIGOLD HL PASTEURISED MILK 200ML',
    barcode: '8888026520014',
    unit: 'CAR',
    supplier: 'Malaysia Dairy Industries',
    warehouse: 'Joo Koon DC (68)',
    swatch: '#8B5A2B',
    image: productMarigoldHlImg,
    onOffer: false,
    inPlanogram: true,
    casePack: 24,
    pending: 0,
    fnr: 18,
    transit: 0,
    soh: 26,
  },
  {
    id: '100055671103',
    name: 'Milo Cereal Breakfast 330g',
    barcode: '9556001234581',
    unit: 'CAR',
    supplier: 'Nestlé Malaysia',
    warehouse: '88 (88)',
    swatch: '#F5A623',
    image: productMiloCerealImg,
    onOffer: false,
    inPlanogram: false,
    casePack: 6,
    pending: 3,
    fnr: 12,
    transit: 0,
    soh: 4,
  },
  {
    id: '200088990011',
    name: 'Maggi 2-Minute Noodles Curry (5-pack)',
    barcode: '9556007788112',
    unit: 'CAR',
    supplier: 'Nestlé Malaysia',
    warehouse: 'Joo Koon DC (68)',
    swatch: '#DE0000',
    image: productMaggiCurryImg,
    onOffer: true,
    inPlanogram: true,
    casePack: 30,
    pending: 20,
    fnr: 60,
    transit: 10,
    soh: 25,
  },
  {
    id: '300012340055',
    name: 'Nescafé Classic Jar 200g',
    barcode: '9556003344551',
    unit: 'CAR',
    supplier: 'Nestlé Malaysia',
    warehouse: '88 (88)',
    swatch: '#4D4D4D',
    image: productNescafeJarImg,
    onOffer: false,
    inPlanogram: true,
    casePack: 12,
    pending: 5,
    fnr: 18,
    transit: 2,
    soh: 9,
  },
]

/** Look up a mock SKU by id; falls back to the first SKU for the scanner demo. */
export function findSku(id: string): Sku {
  return MOCK_SKUS.find((s) => s.id === id) ?? MOCK_SKUS[0]
}

/** The SKU the mock scanner "reads" on tap. */
export const SCANNED_SKU_ID = '300012340055'

// The Milo + Maggi line pair several seed orders share. A factory (not a shared
// const) so each order gets its own item objects — the receiving flow mutates
// grQty in place, and aliasing would leak one order's counts into another.
function standardOrderItems(): Order['items'] {
  return [
    {
      sku: '100055671101',
      name: 'Milo Activ-Go 3-in-1 (30 x 33g)',
      unit: 'CAR',
      swatch: '#7CCC78',
      poQty: 8,
      grQty: 0,
    },
    {
      sku: '200088990011',
      name: 'Maggi 2-Minute Noodles Curry (5-pack)',
      unit: 'CAR',
      swatch: '#DE0000',
      poQty: 5,
      grQty: 0,
    },
  ]
}

export const MOCK_ORDERS: Order[] = [
  {
    id: 'ord-1000',
    orderNumber: 'PO-2026-000832',
    supplier: 'GARDENIA FOOD(S)',
    vendorId: '53225',
    warehouse: 'WH-01 Shah Alam',
    status: 'hold',
    orderedDate: toISODate(new Date()),
    deliveryDate: toISODate(new Date()),
    // Matches the unguided Lesson 4 "straight to QPO" seed (?sku=500011223301)
    // so reopening this held QPO shows the same chilled Fresh Milk line and its
    // temperature-by-class task stays completable.
    items: [
      {
        sku: '500011223301',
        name: 'Fresh Milk Full Cream 1L',
        unit: 'CAR',
        swatch: '#FFFFFF',
        poQty: 12,
        grQty: 12,
      },
    ],
    createdBy: 'Wilson Chu',
  },
  {
    id: 'ord-1001',
    orderNumber: 'PO-2026-000481',
    supplier: 'Nestlé Malaysia',
    vendorId: '52188',
    warehouse: 'WH-01 Shah Alam',
    status: 'pending-approval',
    orderedDate: toISODate(new Date()),
    deliveryDate: toISODate(new Date()),
    items: standardOrderItems(),
    createdBy: 'Wilson Chu',
  },
  {
    id: 'ord-1002',
    orderNumber: 'PO-2026-000479',
    supplier: 'Nestlé Malaysia',
    vendorId: '52188',
    warehouse: 'WH-02 Klang',
    status: 'approved',
    orderedDate: toISODate(new Date()),
    deliveryDate: toISODate(new Date()),
    items: [
      {
        sku: '300012340055',
        name: 'Nescafé Classic Jar 200g',
        unit: 'CAR',
        swatch: '#4D4D4D',
        poQty: 6,
        grQty: 0,
      },
    ],
    createdBy: 'Wilson Chu',
  },
  {
    id: 'ord-1003',
    orderNumber: 'PO-2026-000475',
    supplier: 'Nestlé Malaysia',
    vendorId: '52188',
    warehouse: 'WH-01 Shah Alam',
    status: 'completed',
    orderedDate: toISODate(new Date()),
    deliveryDate: toISODate(new Date()),
    items: [
      {
        sku: '100055671102',
        name: 'Milo Original Powder Refill 1kg',
        unit: 'CAR',
        swatch: '#2E5CD1',
        poQty: 10,
        grQty: 10,
      },
    ],
    createdBy: 'Wilson Chu',
  },
  {
    id: 'ord-1004',
    orderNumber: 'PO-2026-000468',
    supplier: 'Nestlé Malaysia',
    vendorId: '52188',
    warehouse: 'WH-02 Klang',
    status: 'auto-rejected',
    orderedDate: toISODate(new Date()),
    deliveryDate: toISODate(new Date()),
    items: [
      {
        sku: '200088990011',
        name: 'Maggi 2-Minute Noodles Curry (5-pack)',
        unit: 'CAR',
        swatch: '#DE0000',
        poQty: 20,
        grQty: 0,
      },
    ],
    createdBy: 'Wilson Chu',
  },
]

export function findOrder(id: string): Order | undefined {
  return MOCK_ORDERS.find((o) => o.id === id)
}

// ── QPO mock data ──────────────────────────────────────────────────────────

const QPO_LESSON3_SUPPLIER_ENTRY: QpoSupplier = {
  id: '56379',
  name: 'Malaysia Dairy Industries',
  label: '56379_Malaysia Dairy Industries',
}

export const QPO_MOCK_SUPPLIERS: QpoSupplier[] = [
  { id: '53225', name: 'GARDENIA FOOD(S)', label: '53225_GARDENIA FOOD(S)' },
  { id: '63377', name: 'HWA HING', label: '63377_HWA HING' },
  { id: '61234', name: 'YAKIN', label: '61234_YAKIN' },
  { id: '55001', name: 'F&N FOODS', label: '55001_F&N FOODS' },
  QPO_LESSON3_SUPPLIER_ENTRY,
]

/** Supplier used throughout Lesson 3 (Creating a QPO). */
export const QPO_LESSON3_SUPPLIER = QPO_LESSON3_SUPPLIER_ENTRY

const BASE_QPO_ITEMS: QpoItem[] = [
  {
    skuId: '100055671101',
    name: 'Milo Activ-Go 3-in-1 (30 x 33g)',
    unit: 'CAR',
    swatch: '#7CCC78',
    poQty: 10,
    exchQty: 0,
    grQty: 10,
    temp: null,
    tempCategory: 'ambient',
  },
  {
    skuId: '100055671102',
    name: 'Milo Original Powder Refill 1kg',
    unit: 'CAR',
    swatch: '#2E5CD1',
    poQty: 12,
    exchQty: 0,
    grQty: 12,
    temp: null,
    tempCategory: 'ambient',
  },
  {
    skuId: '200088990011',
    name: 'Maggi 2-Minute Noodles Curry (5-pack)',
    unit: 'CAR',
    swatch: '#DE0000',
    poQty: 30,
    exchQty: 0,
    grQty: 30,
    temp: null,
    tempCategory: 'ambient',
  },
  {
    skuId: '500011223301',
    name: 'Fresh Milk Full Cream 1L',
    unit: 'CAR',
    swatch: '#FFFFFF',
    poQty: 12,
    exchQty: 0,
    grQty: 12,
    temp: null,
    tempCategory: 'chilled',
  },
  {
    skuId: 'chilled-001',
    name: 'Tasty Food Chilled Beef',
    unit: 'CAR',
    swatch: '#EF4444',
    poQty: 10,
    exchQty: 0,
    grQty: 10,
    temp: null,
    tempCategory: 'chilled',
  },
  {
    skuId: 'frozen-001',
    name: 'Chef Delights Frozen Beef',
    unit: 'CAR',
    swatch: '#3B82F6',
    poQty: 6,
    exchQty: 0,
    grQty: 6,
    temp: null,
    tempCategory: 'frozen',
  },
]

// Lesson 3 (Creating a QPO): Malaysia Dairy Industries delivery. Two chilled
// SKUs the learner adds one at a time — the milk is scanned, the yoghurt is
// found through search — so the QPO grows from one line to two.
export const QPO_LESSON3_ITEMS: QpoItem[] = [
  {
    skuId: '131467',
    name: 'Marigold HL Pasteurised Milk 1L',
    unit: 'EA',
    swatch: '#E3ECF7',
    poQty: 12,
    exchQty: 0,
    grQty: 12,
    temp: null,
    tempCategory: 'chilled',
  },
  {
    skuId: '340133',
    name: 'Marigold L/Fat Peach Yoghurt 130G',
    unit: 'EA',
    swatch: '#F7C9A8',
    poQty: 24,
    exchQty: 0,
    grQty: 24,
    temp: null,
    tempCategory: 'chilled',
  },
]

// Full QPO catalogue = base delivery plus the Lesson 3 Marigold SKUs. Scan and
// search resolve against this list; the shortfall set below stays the base
// delivery only, so Lesson 5 is unaffected.
export const MOCK_QPO_ITEMS: QpoItem[] = [...BASE_QPO_ITEMS, ...QPO_LESSON3_ITEMS]

export const MOCK_QPO_SHORTFALL_ITEMS: QpoItem[] = [
  { ...(BASE_QPO_ITEMS[0] as QpoItem), grQty: 8 },
  { ...(BASE_QPO_ITEMS[1] as QpoItem), grQty: 10 },
  ...BASE_QPO_ITEMS.slice(2),
]

export const QPO_SCANNED_SKU_ID = '131467'

// Pre-confirmed class temps used by held/history mock sessions.
const CONFIRMED_CLASS_TEMP_MAP = {
  chilled: { temp: '-4', status: 'confirmed' as const },
  frozen: { temp: '-15', status: 'confirmed' as const },
  'ice-cream': { temp: '-20', status: 'confirmed' as const },
}

export function findQpoItem(skuId: string): QpoItem | undefined {
  return MOCK_QPO_ITEMS.find((i) => i.skuId === skuId)
}

/**
 * A SKU added by hand (keyed vendor + SKU) carries no purchase order behind it,
 * so there is no PO / Exchange baseline to prefill or compare against. Zero
 * those out: GR QTY starts empty for the learner to key in, and nothing can read
 * as a shortfall. Only the supplier-QR path (which loads a real PO) keeps them.
 */
export function toManualQpoLine(item: QpoItem): QpoItem {
  return { ...item, poQty: 0, exchQty: 0, grQty: 0 }
}

export const MOCK_HELD_QPOS: QpoSession[] = [
  {
    supplier: { id: '63377', name: 'HWA HING', label: '63377_HWA HING' },
    driverName: 'Ahmad Reza',
    employeeCode: 'E88120',
    vehicleNo: 'WA7732F',
    invoiceNo: 'INV-20260901',
    deliveryDate: randomFutureDate(90),
    status: 'held',
    classTempMap: CONFIRMED_CLASS_TEMP_MAP,
    items: [
      // biome-ignore lint/style/noNonNullAssertion: indices 0 and 1 are guaranteed by the constant definition above
      { ...MOCK_QPO_ITEMS[0]!, grQty: 20 },
      // biome-ignore lint/style/noNonNullAssertion: indices 0 and 1 are guaranteed by the constant definition above
      { ...MOCK_QPO_ITEMS[1]!, grQty: 12 },
    ],
  },
  {
    supplier: { id: '61234', name: 'YAKIN', label: '61234_YAKIN' },
    driverName: 'Siti Farah',
    employeeCode: 'E77441',
    vehicleNo: 'WB3391G',
    invoiceNo: 'INV-20260902',
    deliveryDate: randomFutureDate(90),
    status: 'held',
    classTempMap: CONFIRMED_CLASS_TEMP_MAP,
    items: MOCK_QPO_ITEMS.map((i) => ({ ...i, grQty: i.exchQty })), // all matched
  },
]

export const MOCK_RTVS: RtvCardData[] = [
  {
    id: '6',
    // The same document `/issue-goods?flow=rsto` shows, so l3-14's "find the
    // RSTO" and l3-16's header name one number rather than two. The screen
    // also carries `DO No. 8400874555`; a card has room for one number only.
    number: 'RSTO No. 9500790301',
    vendor: 'WH (68)',
    status: 'pending-gi',
    createdBy: 'Wilson Chu',
    dateLabel: 'Created date',
    date: 'Mon, 12 Aug 2026',
    ctaLabel: 'Issue goods',
    showPrint: true,
    dataTour: 'rsto-issue-goods',
    dataTourWrapper: 'rsto-card',
    dataTourPrint: 'rsto-print-icon',
  },
  {
    id: '1',
    number: 'RTV No. 123455678',
    vendor: 'HWA HING Pte Ltd (71726)',
    status: 'pending-disposal',
    createdBy: 'Wilson Chu',
    dateLabel: 'Creation date',
    date: 'Mon, 12 Aug 2026',
    ctaLabel: 'Proceed to dispose',
    dataTour: 'rtv-proceed-to-dispose',
    dataTourWrapper: 'rtv-disposal-card',
  },
  {
    id: '2',
    number: 'RTV No. 123455678',
    vendor: 'HWA HING Pte Ltd (71726)',
    status: 'pending-gi',
    createdBy: 'Wilson Chu',
    dateLabel: 'Creation date',
    date: 'Mon, 12 Aug 2026',
    ctaLabel: 'Issue goods',
    dataTour: 'rtv-issue-goods',
    dataTourWrapper: 'rtv-pending-gi-card',
  },
  {
    id: '3',
    number: 'RTV No. 123455678',
    vendor: 'HWA HING Pte Ltd (71726)',
    status: 'pending-ack',
    createdBy: 'Wilson Chu',
    dateLabel: 'Creation date',
    date: 'Mon, 12 Aug 2026',
    ctaLabel: 'Issue goods',
  },
  {
    id: '4',
    number: 'Case No. 12345678',
    vendor: 'WH 68',
    status: 'awaiting-approval',
    createdBy: 'Wilson Chu',
    dateLabel: 'Created date',
    date: 'Mon, 12 Aug 2026',
    ctaLabel: 'View details',
    showPrint: true,
    dataTour: 'rtv-view-details',
    dataTourWrapper: 'rtv-awaiting-card',
  },
  {
    id: '5',
    number: 'RTV No. 36253712',
    vendor: 'JJ Fruits Pte Ltd',
    status: 'reminder-sent',
    dateLabel: 'Submission date',
    date: 'Mon, 25 Aug 2026',
    ctaLabel: 'To dispose',
    showPrint: true,
  },
]
// ── Quick Purchase Order (QPO) goods receiving ───────────────────────────────

export const MOCK_SUPPLIERS: [Supplier, ...Supplier[]] = [
  { code: 'SUP-0142', name: 'Nestlé Malaysia', warehouse: 'Joo Koon DC (68)' },
  { code: 'SUP-0207', name: 'Fresh Dairy Farms', warehouse: 'Joo Koon DC (68)' },
  { code: 'SUP-0318', name: 'Golden Poultry Supplies', warehouse: '88 (88)' },
  { code: 'SUP-0451', name: 'Ocean Harvest Seafood', warehouse: '88 (88)' },
]

export function findSupplier(code: string): Supplier | undefined {
  return MOCK_SUPPLIERS.find((s) => s.code === code)
}

/** The supplier the mock QR scanner "reads" on tap. */
export const SCANNED_SUPPLIER_CODE = 'SUP-0207'

/** Driver paperwork the scanned QR label pre-fills. */
export const SCANNED_SUPPLIER_DETAILS = {
  driverName: 'Rahim bin Osman',
  employeeCode: 'EMP4417',
  vehicleNumber: 'WXY4417',
} as const

/**
 * The delivery lines on a QPO. Every SKU here is a chilled good, so
 * `CHILLED_TEMP_RANGE` applies to all of them.
 *
 * Deliberately seeded with `grQty`/`measuredTempC` unset: the whole point of the
 * screen is that the storekeeper counts and probes each line themselves.
 */
export const MOCK_QPO_LINES: [QpoLine, ...QpoLine[]] = [
  {
    lineId: 'qpo-line-1',
    sku: '500011223301',
    name: 'Fresh Milk Full Cream 1L',
    unit: 'CAR',
    swatch: '#2E5CD1',
    poQty: 12,
    grQty: null,
    measuredTempC: null,
    tempAccepted: false,
  },
  {
    lineId: 'qpo-line-2',
    sku: '500011223318',
    name: 'Greek Yoghurt Natural 500g',
    unit: 'CAR',
    swatch: '#7CCC78',
    poQty: 8,
    grQty: null,
    measuredTempC: null,
    tempAccepted: false,
  },
  {
    lineId: 'qpo-line-3',
    sku: '500011223325',
    name: 'Chilled Chicken Whole (per kg)',
    unit: 'CAR',
    swatch: '#F5A623',
    poQty: 6,
    grQty: null,
    measuredTempC: null,
    tempAccepted: false,
  },
]

/**
 * Reference numbers are handed out sequentially from here so the sandbox is
 * deterministic — a training screenshot always shows the same first reference.
 */
export const QPO_REFERENCE_START = 116

/** Format a QPO reference from its sequence number. */
export function formatQpoReference(sequence: number): string {
  return `QPO-2026-${String(sequence).padStart(6, '0')}`
}

/** Shown on the confirmation screen when it is opened without a submitted draft. */
export const FALLBACK_QPO_REFERENCE = formatQpoReference(QPO_REFERENCE_START + 1)

// ── Goods-receipt history (Lesson 4) ─────────────────────────────────────────
// Two completed deliveries: one received in full, one with a shortfall, so the
// history filter chips have something to distinguish.

export const MOCK_GOODS_RECEIPTS: [GoodsReceipt, ...GoodsReceipt[]] = [
  {
    id: 'gr-2026-000117',
    reference: 'QPO-2026-000117',
    supplier: '63377 HWA HING TRADING',
    driverName: 'Rahman bin Yusof',
    vehicleNumber: 'WXY 4471',
    deliveryDate: '2026-09-09',
    receivedBy: 'Amirah binti Hassan',
    totalValue: 1284.5,
    lines: [
      {
        sku: '500011223301',
        name: 'Fresh Milk Full Cream 1L',
        unit: 'CAR',
        swatch: '#2E5CD1',
        poQty: 12,
        receivedQty: 1,
      },
      {
        sku: '500011223318',
        name: 'Greek Yoghurt Natural 500g',
        unit: 'CAR',
        swatch: '#7CCC78',
        poQty: 6,
        receivedQty: 6,
      },
    ],
  },
  {
    id: 'gr-2026-000116',
    reference: 'QPO-2026-000116',
    supplier: '52001 GARDENIA FOODS PTE LTD',
    driverName: 'Lim Wei Sheng',
    vehicleNumber: 'SGX 8823',
    deliveryDate: '2026-09-05',
    receivedBy: 'Kevin Wu',
    totalValue: 642.0,
    lines: [
      {
        sku: '100055671101',
        name: 'Milo Activ-Go 3-in-1 (30 x 33g)',
        unit: 'CAR',
        swatch: '#F5A623',
        poQty: 8,
        receivedQty: 8,
      },
    ],
  },
]

export function findGoodsReceipt(id: string): GoodsReceipt | undefined {
  return MOCK_GOODS_RECEIPTS.find((r) => r.id === id)
}
